/**
 * Phase Perf-1 (13/09/2026) — Page /performance (Vue Admin uniquement).
 *
 * Classement des commerciaux du tenant sur une période paramétrable, avec 6 KPI :
 *   - RDV tenus (Rendezvous.realization_status="effectue")
 *   - Taux de débrief complété (KPI clé, mise en avant)
 *   - Devis envoyés
 *   - Devis signés (avenants exclus, décision D2)
 *   - Taux de conversion vs cible 33% (mise en avant, coloration)
 *   - Montant signé TVAC
 *
 * RBAC strict :
 *   - Frontend : composant redirige vers /dashboard si non-admin.
 *   - Backend : endpoint `require_roles({ADMIN})` → 403 si non-admin
 *     (défense en profondeur).
 *
 * Bandeau "Données à partir du 01/09/2026" affiché si la période inclut du
 * legacy pré-Phase D1 (avant `realization_status`).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { hasRole } from "@/lib/roles";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, TrendingUp, AlertTriangle, Target, Calendar } from "lucide-react";

const MONEY = (n) =>
  new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const PCT = (n) =>
  `${(Number(n) * 100).toLocaleString("fr-BE", { minimumFractionDigits: 0, maximumFractionDigits: 1 })} %`;

const PERIOD_PRESETS = [
  { value: "15d", label: "15 derniers jours" },
  { value: "current_month", label: "Ce mois" },
  { value: "last_month", label: "Mois dernier" },
  { value: "custom", label: "Personnalisé" },
];

// Coloration cible 33 % (décision produit)
const conversionTone = (rate) => {
  if (rate >= 0.33) return { color: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/40", label: "Au-dessus cible" };
  if (rate >= 0.2) return { color: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/40", label: "Sous cible" };
  return { color: "text-red-300", bg: "bg-red-500/10", border: "border-red-500/40", label: "Alerte" };
};

export default function PerformancePage() {
  const { state } = useAuth();
  const user = state?.user;
  const isAdmin = hasRole(user, ["admin"]);

  const [period, setPeriod] = useState("15d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const params = { period };
      if (period === "custom") {
        if (!customFrom || !customTo) {
          setLoading(false);
          return;
        }
        params.date_from = customFrom;
        params.date_to = customTo;
      }
      const { data } = await api.get("/reports/commercial-performance", { params });
      setData(data);
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, period, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  // Défense en profondeur — redirige les non-admin vers /dashboard.
  if (state && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const rows = data?.commercials || [];
  const label = data?.period?.label || "";
  const includesLegacy = !!data?.period?.includes_pre_d1_legacy;

  return (
    <div className="space-y-6" data-testid="perf-admin-page">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-100">
            <TrendingUp className="h-6 w-6 text-[#C4553B]" />
            Performance commerciale
          </h1>
          <p className="mt-1 max-w-xl text-sm text-slate-400">
            Classement des commerciaux sur la période sélectionnée. Cible de conversion :{" "}
            <span className="font-semibold text-slate-200">1/3 (33 %)</span>.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Target className="h-3 w-3" />
          Vue Admin — les commerciaux n&apos;accèdent pas à ce classement.
        </div>
      </header>

      {/* Sélecteur période */}
      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
            <Calendar className="h-3 w-3" /> Période
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="perf-period-selector">
            {PERIOD_PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setPeriod(p.value)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                  period === p.value
                    ? "border-[#C4553B] bg-[#C4553B]/20 text-[#C4553B]"
                    : "border-slate-700 bg-slate-950/40 text-slate-400 hover:text-slate-200"
                }`}
                data-testid={`perf-period-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {period === "custom" && (
            <div
              className="grid gap-3 sm:grid-cols-2 max-w-md"
              data-testid="perf-period-custom-inputs"
            >
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Du</Label>
                <Input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="border-slate-700 bg-slate-950/40 text-slate-100"
                  data-testid="perf-period-from"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Au</Label>
                <Input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="border-slate-700 bg-slate-950/40 text-slate-100"
                  data-testid="perf-period-to"
                />
              </div>
              <div className="sm:col-span-2">
                <Button
                  size="sm"
                  onClick={load}
                  disabled={!customFrom || !customTo || loading}
                  className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
                  data-testid="perf-period-apply"
                >
                  Appliquer
                </Button>
              </div>
            </div>
          )}
          {label && !loading && (
            <div className="text-xs text-slate-500" data-testid="perf-period-label">
              Période : <span className="font-medium text-slate-300">{label}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bandeau legacy pré-D1 */}
      {includesLegacy && (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm text-amber-200"
          data-testid="perf-legacy-banner"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <div className="font-medium">Données à partir du 01/09/2026</div>
            <p className="mt-0.5 text-xs text-amber-200/80">
              Les rendez-vous antérieurs à cette date ne renseignent pas leur statut de
              réalisation (Phase D1) et sont exclus des KPI. La période sélectionnée
              inclut du legacy — les compteurs peuvent sous-estimer l&apos;activité réelle.
            </p>
          </div>
        </div>
      )}

      {/* Tableau classement */}
      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center gap-2 p-8 text-slate-400" data-testid="perf-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
            </div>
          ) : error ? (
            <div className="p-8 text-sm text-red-300" data-testid="perf-error">
              {error}
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-sm text-slate-400" data-testid="perf-empty">
              Aucun commercial dans ce tenant sur la période.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="min-w-full divide-y divide-slate-800 text-sm"
                data-testid="perf-admin-table"
              >
                <thead className="bg-slate-950/60 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="p-3 text-left">Commercial</th>
                    <th className="p-3 text-right">RDV tenus</th>
                    <th className="p-3 text-right">Taux de débrief</th>
                    <th className="p-3 text-right">Devis envoyés</th>
                    <th className="p-3 text-right">Devis signés</th>
                    <th className="p-3 text-right">Taux conversion (cible 33 %)</th>
                    <th className="p-3 text-right">Montant signé TVAC</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800 text-slate-100">
                  {rows.map((r, i) => {
                    const tone = conversionTone(r.conversion_rate);
                    return (
                      <tr
                        key={r.user_id}
                        className="hover:bg-slate-800/30"
                        data-testid={`perf-row-${r.user_id}`}
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-800 text-xs font-semibold text-slate-300"
                              data-testid={`perf-rank-${r.user_id}`}
                            >
                              #{i + 1}
                            </span>
                            <div>
                              <div className="font-medium" data-testid={`perf-name-${r.user_id}`}>
                                {r.name}
                              </div>
                              <div className="text-xs text-slate-500">{r.email}</div>
                            </div>
                          </div>
                        </td>
                        <td
                          className="p-3 text-right tabular-nums"
                          data-testid={`perf-rdv-held-${r.user_id}`}
                        >
                          {r.rdv_held}
                        </td>
                        <td className="p-3 text-right tabular-nums">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${
                              r.debrief_completion_rate >= 0.8
                                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                                : r.debrief_completion_rate >= 0.5
                                ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
                                : "border-red-500/40 bg-red-500/10 text-red-300"
                            }`}
                            data-testid={`perf-debrief-${r.user_id}`}
                          >
                            {PCT(r.debrief_completion_rate)}
                          </span>
                        </td>
                        <td
                          className="p-3 text-right tabular-nums"
                          data-testid={`perf-quotes-sent-${r.user_id}`}
                        >
                          {r.quotes_sent}
                        </td>
                        <td
                          className="p-3 text-right tabular-nums"
                          data-testid={`perf-quotes-signed-${r.user_id}`}
                        >
                          {r.quotes_signed}
                        </td>
                        <td className="p-3 text-right tabular-nums">
                          <Badge
                            className={`border ${tone.border} ${tone.bg} ${tone.color} text-xs font-semibold`}
                            data-testid={`perf-conversion-${r.user_id}`}
                          >
                            {PCT(r.conversion_rate)}
                          </Badge>
                          <div className="mt-0.5 text-[10px] text-slate-500">{tone.label}</div>
                        </td>
                        <td
                          className="p-3 text-right tabular-nums font-medium"
                          data-testid={`perf-amount-${r.user_id}`}
                        >
                          {MONEY(r.signed_amount_tvac)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
